import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import { supabase } from '../lib/supabase';
import { productUrl, crafterUrl } from '../utils/urls';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Heart, 
  Package, 
  Store, 
  Trash2, 
  ExternalLink,
  Loader2,
  ShoppingBag,
  Euro,
  Star,
  MapPin
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { sortProductsByAvailability } from '../utils/productAvailability';

export default function FavoritesPage({ embedded = false }) {
  const { user, loading: authLoading } = useAuth();
  const { favorites, loading: favoritesLoading, removeFavorite, getFavoritesByType } = useFavorites();
  
  const [products, setProducts] = useState([]);
  const [crafters, setCrafters] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [activeTab, setActiveTab] = useState('products');

  // Load product and crafter details
  useEffect(() => {
    const loadDetails = async () => {
      if (favoritesLoading) return;
      
      setLoadingDetails(true);
      
      try {
        // Get favorite product IDs
        const productFavorites = getFavoritesByType('product');
        const crafterFavorites = getFavoritesByType('crafter');
        
        // Load products
        if (productFavorites.length > 0) {
          const productIds = productFavorites.map(f => f.item_id);
          const { data: productsData } = await supabase
            .from('products')
            .select('*, profiles!products_crafter_id_fkey(full_name, avatar_url, crafter_type)')
            .in('id', productIds);
          
          if (productsData) {
            // Apply availability sorting - out-of-stock products at the end
            setProducts(sortProductsByAvailability(productsData));
          }
        } else {
          setProducts([]);
        }
        
        // Load crafters
        if (crafterFavorites.length > 0) {
          const crafterIds = crafterFavorites.map(f => f.item_id);
          const { data: craftersData } = await supabase
            .from('profiles')
            .select('*')
            .in('id', crafterIds)
            .eq('role', 'crafter');
          
          if (craftersData) {
            setCrafters(craftersData);
          }
        } else {
          setCrafters([]);
        }
      } catch (error) {
        console.error('Error loading favorite details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };
    
    loadDetails();
  }, [favorites, favoritesLoading, getFavoritesByType]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleRemoveProduct = async (productId) => {
    await removeFavorite(productId, 'product');
    setProducts(prev => prev.filter(p => p.id !== productId));
  };

  const handleRemoveCrafter = async (crafterId) => {
    await removeFavorite(crafterId, 'crafter');
    setCrafters(prev => prev.filter(c => c.id !== crafterId));
  };

  const isLoading = favoritesLoading || loadingDetails;

  return (
    <div className="min-h-screen bg-slate-50 py-8" data-testid="favorites-page">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800">I Miei Preferiti</h1>
              <p className="text-slate-600">Prodotti e artigiani che hai salvato</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="products" className="gap-2">
              <Package className="w-4 h-4" />
              Prodotti ({products.length})
            </TabsTrigger>
            <TabsTrigger value="crafters" className="gap-2">
              <Store className="w-4 h-4" />
              Artigiani ({crafters.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            {isLoading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#008080] mx-auto" />
              </div>
            ) : products.length === 0 ? (
              <Card className="p-12 text-center">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Nessun prodotto salvato</h3>
                <p className="text-slate-500 mb-6">
                  Esplora il catalogo e salva i prodotti che ti interessano
                </p>
                <Button asChild className="bg-[#008080] hover:bg-[#006666]">
                  <Link to="/esplora">
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Esplora i prodotti
                  </Link>
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                  {products.map((product) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      layout
                    >
                      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                        <Link to={productUrl(product)}>
                          <div className="aspect-square relative">
                            <img
                              src={product.images?.[0] || '/placeholder-product.jpg'}
                              alt={product.title}
                              className="w-full h-full object-cover"
                            />
                            {product.profiles?.crafter_type === 'professionista' && (
                              <Badge className="absolute top-3 left-3 bg-amber-500">
                                Pro
                              </Badge>
                            )}
                          </div>
                        </Link>
                        <div className="p-4">
                          <Link to={productUrl(product)}>
                            <h3 className="font-semibold text-slate-800 hover:text-[#008080] transition-colors line-clamp-1">
                              {product.title}
                            </h3>
                          </Link>
                          <p className="text-sm text-slate-500 mt-1">
                            di {product.profiles?.full_name || 'Artigiano'}
                          </p>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-1 text-lg font-bold text-[#008080]">
                              <Euro className="w-4 h-4" />
                              {product.base_price?.toFixed(2)}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveProduct(product.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Rimuovi
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>

          {/* Crafters Tab */}
          <TabsContent value="crafters">
            {isLoading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#008080] mx-auto" />
              </div>
            ) : crafters.length === 0 ? (
              <Card className="p-12 text-center">
                <Store className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">Nessun artigiano salvato</h3>
                <p className="text-slate-500 mb-6">
                  Scopri gli artigiani italiani e salva i tuoi preferiti
                </p>
                <Button asChild className="bg-[#DAA520] hover:bg-[#B8860B]">
                  <Link to="/crafters">
                    <Store className="w-4 h-4 mr-2" />
                    Scopri gli Artigiani
                  </Link>
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence>
                  {crafters.map((crafter) => (
                    <motion.div
                      key={crafter.id}
                      initial={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      layout
                    >
                      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                        <Link to={crafterUrl(crafter)}>
                          <div className="h-32 bg-gradient-to-br from-[#008080] to-[#006666] relative">
                            {crafter.storefront_image && (
                              <img
                                src={crafter.storefront_image}
                                alt={crafter.storefront_title}
                                className="w-full h-full object-cover opacity-50"
                              />
                            )}
                            <div className="absolute -bottom-10 left-4">
                              <div className="w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden shadow-lg">
                                {crafter.avatar_url ? (
                                  <img
                                    src={crafter.avatar_url}
                                    alt={crafter.full_name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-[#008080] flex items-center justify-center text-white text-2xl font-bold">
                                    {crafter.full_name?.charAt(0) || 'A'}
                                  </div>
                                )}
                              </div>
                            </div>
                            {crafter.crafter_type === 'professionista' && (
                              <Badge className="absolute top-3 right-3 bg-amber-500">
                                <Star className="w-3 h-3 mr-1" />
                                Pro
                              </Badge>
                            )}
                          </div>
                        </Link>
                        <div className="pt-12 p-4">
                          <Link to={crafterUrl(crafter)}>
                            <h3 className="font-semibold text-slate-800 hover:text-[#008080] transition-colors">
                              {crafter.storefront_title || crafter.full_name}
                            </h3>
                          </Link>
                          <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                            {crafter.storefront_description || 'Artigiano su Rarifex'}
                          </p>
                          {crafter.location && (
                            <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                              <MapPin className="w-3 h-3" />
                              {crafter.location}
                            </div>
                          )}
                          <div className="flex items-center justify-between mt-4 pt-3 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                            >
                              <Link to={crafterUrl(crafter)}>
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Visita
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveCrafter(crafter.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-crafter-${crafter.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Rimuovi
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
